<template>
    <b-modal
            :ok-only="true"
            size="xl"
            title="QR Scan"
            :id="id"
            @shown="onModalShown"
            @hidden="onModalHidden">
        <b-container>
            <b-row>
                <b-col v-if="error">
                    <b-alert variant="warning" show>{{ errorMessage }}</b-alert>
                </b-col>
                <b-col v-else>
                    <div class="qr-stream-wrap"
                         ref="wrap"
                         @pointerdown="onPointerDown"
                         @pointermove="onPointerMove"
                         @pointerup="onPointerUp"
                         @pointercancel="onPointerUp"
                         @pointerleave="onPointerUp">
                        <qrcode-stream
                                ref="stream"
                                :camera="camera"
                                :track="paintTrack"
                                @init="onInit"
                                @decode="onDecode">
                            <div v-if="reticleRect"
                                 class="reticle"
                                 :class="{'reticle-success': success}"
                                 :style="reticleStyle"></div>
                            <b-button
                                    v-if="torchSupported"
                                    class="torch-btn"
                                    size="sm"
                                    :variant="torchOn ? 'primary' : 'light'"
                                    @click.stop="toggleTorch">
                                <i class="fal fa-bolt"></i>
                            </b-button>
                        </qrcode-stream>
                    </div>
                    <p class="text-center text-muted small mt-2 mb-0" aria-hidden="true">
                        <strong v-if="outsideHint">Move the code into the frame.</strong>
                        <template v-else>Hold the code inside the frame &mdash; if it stays blurry, move back and zoom in.</template>
                    </p>
                    <div v-if="zoomCaps" class="d-flex align-items-center mt-2">
                        <i class="fal fa-search-minus mr-2"></i>
                        <b-form-input
                                type="range"
                                aria-label="Camera zoom"
                                :min="zoomCaps.min"
                                :max="zoomCaps.max"
                                :step="zoomCaps.step"
                                v-model.number="zoomValue"
                                @input="scheduleZoom"/>
                        <i class="fal fa-search-plus ml-2"></i>
                    </div>
                    <div class="sr-only" aria-live="polite">{{ announcement }}</div>
                </b-col>
            </b-row>
        </b-container>
    </b-modal>
</template>


<script>

    // fraction of the viewport's smaller dimension covered by the aiming frame
    const RETICLE_SIZE = 0.65;

    export default {
        name: 'qr-search',
        components: {},
        props: {
            id: String
        },
        data: function () {
            return {
                busy: false,
                camera: "off",
                error: null,
                errorMessage: "",
                currentContent: null,
                navigating: false,
                success: false,
                outsideHint: false,
                announcement: "",
                reticleRect: null,
                zoomCaps: null,
                zoomValue: 1,
                torchSupported: false,
                torchOn: false
            }
        },
        computed: {
            reticleStyle() {
                if (!this.reticleRect) return {};
                return {
                    left: this.reticleRect.x + "px",
                    top: this.reticleRect.y + "px",
                    width: this.reticleRect.size + "px",
                    height: this.reticleRect.size + "px"
                };
            }
        },
        created() {
            // pinch state is transient and non-reactive
            this._pointers = new Map();
            this._pinchStartDist = 0;
            this._pinchStartZoom = 1;
            this._zoomRaf = null;
            this._navigateTimer = null;
            this._outsideHintTimer = null;
            this._maskCanvas = null;
            this._maskCtx = null;
        },
        beforeDestroy() {
            if (this._zoomRaf) {
                cancelAnimationFrame(this._zoomRaf);
            }
            clearTimeout(this._navigateTimer);
            clearTimeout(this._outsideHintTimer);
            window.removeEventListener("resize", this.updateReticle);
        },
        methods: {
            onModalShown() {
                this.error = null;
                this.errorMessage = "";
                this.currentContent = null;
                this.navigating = false;
                this.success = false;
                this.outsideHint = false;
                this.announcement = "";
                this.camera = "auto";
                window.addEventListener("resize", this.updateReticle);
                this.$nextTick(this.updateReticle);
            },
            onModalHidden() {
                this.camera = "off";
                this.zoomCaps = null;
                this.torchSupported = false;
                this.torchOn = false;
                this._pointers.clear();
                this._pinchStartDist = 0;
                clearTimeout(this._navigateTimer);
                clearTimeout(this._outsideHintTimer);
                window.removeEventListener("resize", this.updateReticle);
            },
            updateReticle() {
                const wrap = this.$refs.wrap;
                if (!wrap) return;
                const w = wrap.offsetWidth;
                const h = wrap.offsetHeight;
                if (!w || !h) return;
                const size = Math.round(Math.min(w, h) * RETICLE_SIZE);
                this.reticleRect = {
                    x: Math.round((w - size) / 2),
                    y: Math.round((h - size) / 2),
                    size
                };
            },
            isInReticle(x, y) {
                const r = this.reticleRect;
                return !!r
                    && x >= r.x && x <= r.x + r.size
                    && y >= r.y && y <= r.y + r.size;
            },
            // called by qrcode-stream for every frame with a located code;
            // coordinates arrive already mapped to display space
            paintTrack(location, ctx) {
                const corners = [
                    location.topLeftCorner,
                    location.topRightCorner,
                    location.bottomRightCorner,
                    location.bottomLeftCorner
                ].filter(Boolean);
                if (corners.length < 4) return;

                const cx = corners.reduce((sum, c) => sum + c.x, 0) / corners.length;
                const cy = corners.reduce((sum, c) => sum + c.y, 0) / corners.length;
                const hit = this.isInReticle(cx, cy);

                ctx.strokeStyle = hit ? "#28a745" : "rgba(255, 255, 255, 0.7)";
                ctx.lineWidth = hit ? 4 : 2;
                ctx.beginPath();
                ctx.moveTo(corners[0].x, corners[0].y);
                corners.slice(1).forEach(c => ctx.lineTo(c.x, c.y));
                ctx.closePath();
                ctx.stroke();

                if (hit) {
                    this.onReticleHit();
                } else {
                    this.showOutsideHint();
                }
            },
            onReticleHit() {
                if (this.navigating || !this.currentContent) return;
                this.navigating = true;
                this.success = true;
                this.outsideHint = false;
                this.announcement = "QR code scanned, opening result.";
                if (navigator.vibrate) {
                    navigator.vibrate(50);
                }
                // freeze on the captured frame while the success flash plays
                this.camera = "off";
                this._navigateTimer = setTimeout(() => {
                    window.location.href = this.currentContent;
                }, 400);
            },
            showOutsideHint() {
                if (this.navigating) return;
                this.outsideHint = true;
                clearTimeout(this._outsideHintTimer);
                this._outsideHintTimer = setTimeout(() => {
                    this.outsideHint = false;
                }, 1500);
            },
            onDecode(decodedString) {
                if (decodedString) {
                    this.currentContent = decodedString;
                }
            },
            onInit(qrCodePromise) {
                return qrCodePromise.then(() => {
                    this.updateReticle();
                    return this.setupTrack();
                }).catch((error) => {
                    this.error = error;
                    this.errorMessage = this.describeError(error);
                })
            },
            describeError(error) {
                switch (error && error.name) {
                    case "NotAllowedError":
                        return "Camera access was denied. Please allow camera access for this site in your browser settings, then reopen the scanner.";
                    case "NotFoundError":
                    case "OverconstrainedError":
                        return "No suitable camera was found on this device.";
                    case "NotReadableError":
                    case "AbortError":
                        return "The camera is already in use by another application.";
                    case "InsecureContextError":
                        return "Camera access requires a secure (HTTPS) connection.";
                    default:
                        return "Could not find or access camera.";
                }
            },
            getVideoTrack() {
                const video = this.$refs.stream && this.$refs.stream.$refs.video;
                const stream = video && video.srcObject;
                return (stream && stream.getVideoTracks()[0]) || null;
            },
            async setupTrack() {
                const track = this.getVideoTrack();
                if (!track) return;

                // vue-qrcode-reader requests only 640x480; small codes need more
                // pixels to decode, so raise the resolution after the fact
                try {
                    await track.applyConstraints({
                        width: {ideal: 1920},
                        height: {ideal: 1080}
                    });
                } catch (e) { /* keep default resolution */ }

                const caps = (track.getCapabilities && track.getCapabilities()) || {};

                if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
                    try {
                        await track.applyConstraints({advanced: [{focusMode: "continuous"}]});
                    } catch (e) { /* focus stays on browser default */ }
                }

                if (caps.zoom && caps.zoom.max > caps.zoom.min) {
                    const settings = (track.getSettings && track.getSettings()) || {};
                    this.zoomCaps = {
                        min: caps.zoom.min,
                        max: caps.zoom.max,
                        step: caps.zoom.step || (caps.zoom.max - caps.zoom.min) / 20
                    };
                    this.zoomValue = settings.zoom || caps.zoom.min;
                } else {
                    this.zoomCaps = null;
                }

                this.torchSupported = !!caps.torch;
                this.torchOn = false;

                this.setupMaskedCapture();
            },
            // map a display-space rect back to camera-frame space
            // (inverse of the object-fit: cover crop)
            reticleFrameRect(video) {
                const r = this.reticleRect;
                const dispW = video.offsetWidth;
                const dispH = video.offsetHeight;
                const resW = video.videoWidth;
                const resH = video.videoHeight;
                if (!r || !dispW || !dispH || !resW || !resH) return null;

                const ratio = Math.max(dispW / resW, dispH / resH);
                const xOff = (dispW - resW * ratio) / 2;
                const yOff = (dispH - resH * ratio) / 2;

                // margin keeps codes just outside the frame locatable,
                // so the dim-outline "move into the frame" feedback still works
                const pad = r.size * 0.35;
                let x = (r.x - pad - xOff) / ratio;
                let y = (r.y - pad - yOff) / ratio;
                let size = (r.size + 2 * pad) / ratio;

                x = Math.max(0, x);
                y = Math.max(0, y);
                const w = Math.min(size, resW - x);
                const h = Math.min(size, resH - y);
                if (w <= 0 || h <= 0) return null;

                return {x, y, w, h};
            },
            // The library decodes through a fixed 1920x1080 landscape canvas.
            // Portrait frames (1080x1920) get downscaled before decoding, but
            // corner coordinates are mapped back to screen assuming full
            // resolution — misplacing the outline and breaking the reticle
            // hit test. Capturing frames ourselves at full resolution keeps
            // coordinates consistent; masking to the aiming frame also stops
            // codes outside it (even off-screen ones) from confusing jsQR,
            // which handles multiple codes per frame badly.
            setupMaskedCapture() {
                const streamComp = this.$refs.stream;
                const cam = streamComp && streamComp.cameraInstance;
                const video = streamComp && streamComp.$refs.video;
                if (!cam || !video || cam._reticleMasked) return;
                cam._reticleMasked = true;

                if (!this._maskCanvas) {
                    this._maskCanvas = document.createElement("canvas");
                    this._maskCtx = this._maskCanvas.getContext("2d");
                }

                cam.captureFrame = () => {
                    const w = video.videoWidth;
                    const h = video.videoHeight;
                    const rect = this.reticleFrameRect(video);
                    const canvas = this._maskCanvas;
                    const ctx = this._maskCtx;

                    if (canvas.width !== w) canvas.width = w;
                    if (canvas.height !== h) canvas.height = h;

                    if (rect) {
                        ctx.fillStyle = "#000";
                        ctx.fillRect(0, 0, w, h);
                        ctx.drawImage(video,
                            rect.x, rect.y, rect.w, rect.h,
                            rect.x, rect.y, rect.w, rect.h);
                    } else {
                        ctx.drawImage(video, 0, 0, w, h);
                    }

                    return ctx.getImageData(0, 0, w, h);
                };
            },
            applyZoom() {
                const track = this.getVideoTrack();
                if (!track || !this.zoomCaps) return;
                track.applyConstraints({advanced: [{zoom: this.zoomValue}]}).catch(() => {});
            },
            scheduleZoom() {
                // some devices apply zoom slowly; batch updates per frame
                if (this._zoomRaf) return;
                this._zoomRaf = requestAnimationFrame(() => {
                    this._zoomRaf = null;
                    this.applyZoom();
                });
            },
            pointerDistance() {
                const [a, b] = [...this._pointers.values()];
                return Math.hypot(a.x - b.x, a.y - b.y);
            },
            onPointerDown(event) {
                if (!this.zoomCaps) return;
                this._pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
                if (this._pointers.size === 2) {
                    this._pinchStartDist = this.pointerDistance();
                    this._pinchStartZoom = this.zoomValue;
                }
            },
            onPointerMove(event) {
                if (!this._pointers.has(event.pointerId)) return;
                this._pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
                if (this._pointers.size === 2 && this._pinchStartDist > 0) {
                    const scale = this.pointerDistance() / this._pinchStartDist;
                    this.zoomValue = Math.min(this.zoomCaps.max,
                        Math.max(this.zoomCaps.min, this._pinchStartZoom * scale));
                    this.scheduleZoom();
                }
            },
            onPointerUp(event) {
                this._pointers.delete(event.pointerId);
                this._pinchStartDist = 0;
            },
            toggleTorch() {
                const track = this.getVideoTrack();
                if (!track) return;
                const next = !this.torchOn;
                track.applyConstraints({advanced: [{torch: next}]}).then(() => {
                    this.torchOn = next;
                }).catch(() => {});
            }
        }
    }
</script>

<style lang="scss" scoped>

    @import "~@shared/_assets/scss/constants";

    .qr-stream-wrap {
        position: relative;
        height: 60vh;
        overflow: hidden;
        background: #000;
        // keep two-finger pinch for camera zoom instead of page zoom/scroll
        touch-action: none;
    }

    .reticle {
        position: absolute;
        border: 2px solid rgba(255, 255, 255, 0.9);
        border-radius: 12px;
        // oversized shadow doubles as the scrim around the aiming frame
        box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
        transition: border-color 0.15s;
        pointer-events: none;
    }

    .reticle-success {
        border-color: #28a745;
        animation: reticle-flash 0.4s;
    }

    @keyframes reticle-flash {
        0% {
            background: rgba(40, 167, 69, 0.4);
        }
        100% {
            background: transparent;
        }
    }

    .torch-btn {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        z-index: 10;
    }

</style>
